import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
const LIKELIHOODS = ['Low', 'Possible', 'Likely'];
const SEVERITIES = ['Minor injury', 'Serious injury', 'Fatality potential'];
const getRiskLevel = (l: string, s: string) => {
  if (s === 'Fatality potential' || s === 'Serious injury' && l === 'Likely') return 'HIGH';
  if (l === 'Low' && s === 'Minor injury') return 'LOW';
  return 'MEDIUM';
};
export function RiskEvaluationScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const assessment = data.riskAssessments[0];
  const [likelihood, setLikelihood] = useState(assessment.likelihood);
  const [severity, setSeverity] = useState(assessment.severity);
  const currentRisk = getRiskLevel(likelihood, severity);
  const handleContinue = () => {
    const updated = [...data.riskAssessments];
    updated[0] = {
      ...updated[0],
      likelihood,
      severity,
      riskLevel: currentRisk as any
    };
    updateData({
      riskAssessments: updated
    });
    onNext();
  };
  return <div className="flex flex-col h-full" data-id="element-396">
      <div className="mb-6" data-id="element-397">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-398">Risk Assessment</h2>
        <p className="text-slate-400" data-id="element-399">
          Evaluate the risk for identified hazards.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2" data-id="element-400">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-8 flex items-start gap-3" data-id="element-401">
          <AlertTriangleIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-402" />
          <div data-id="element-403">
            <p className="text-xs text-yellow-400 uppercase tracking-wider font-bold mb-1" data-id="element-404">
              Evaluating Hazard
            </p>
            <p className="text-white font-medium text-lg" data-id="element-405">
              {assessment.hazard}
            </p>
          </div>
        </div>

        <div className="space-y-8" data-id="element-406">
          <div data-id="element-407">
            <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-408">
              Likelihood
            </p>
            <div className="grid grid-cols-3 gap-2" data-id="element-409">
              {LIKELIHOODS.map(l => <button key={l} onClick={() => setLikelihood(l)} className={`py-3 px-2 rounded-lg text-sm font-medium transition-colors border-2 ${likelihood === l ? 'bg-slate-800 border-yellow-500 text-white' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-410">
                  {l}
                </button>)}
            </div>
          </div>

          <div data-id="element-411">
            <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-412">
              Severity
            </p>
            <div className="flex flex-col gap-2" data-id="element-413">
              {SEVERITIES.map(s => <button key={s} onClick={() => setSeverity(s)} className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors border-2 text-left ${severity === s ? 'bg-slate-800 border-yellow-500 text-white' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-414">
                  {s}
                </button>)}
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-800 flex items-center justify-between" data-id="element-415">
            <div data-id="element-416">
              <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1" data-id="element-417">
                Calculated Risk
              </p>
              <motion.div key={currentRisk} initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className={`text-4xl font-black tracking-tight ${currentRisk === 'HIGH' ? 'text-red-500 drop-shadow-[0_0_15px_rgba(239,68,68,0.5)]' : currentRisk === 'MEDIUM' ? 'text-amber-500 drop-shadow-[0_0_15px_rgba(245,158,11,0.5)]' : 'text-green-500 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]'}`} data-id="element-418">
                {currentRisk}
              </motion.div>
            </div>

            <div className="w-16 h-16 grid grid-cols-3 grid-rows-3 gap-0.5 bg-slate-800 p-0.5 rounded" data-id="element-419">
              <div className={`bg-amber-500/20 ${severity === 'Fatality potential' && likelihood === 'Low' ? 'bg-amber-500' : ''}`} data-id="element-420" />
              <div className={`bg-red-500/20 ${severity === 'Fatality potential' && likelihood === 'Possible' ? 'bg-red-500' : ''}`} data-id="element-421" />
              <div className={`bg-red-500/20 ${severity === 'Fatality potential' && likelihood === 'Likely' ? 'bg-red-500' : ''}`} data-id="element-422" />
              <div className={`bg-green-500/20 ${severity === 'Serious injury' && likelihood === 'Low' ? 'bg-green-500' : ''}`} data-id="element-423" />
              <div className={`bg-amber-500/20 ${severity === 'Serious injury' && likelihood === 'Possible' ? 'bg-amber-500' : ''}`} data-id="element-424" />
              <div className={`bg-red-500/20 ${severity === 'Serious injury' && likelihood === 'Likely' ? 'bg-red-500' : ''}`} data-id="element-425" />
              <div className={`bg-green-500/20 ${severity === 'Minor injury' && likelihood === 'Low' ? 'bg-green-500' : ''}`} data-id="element-426" />
              <div className={`bg-green-500/20 ${severity === 'Minor injury' && likelihood === 'Possible' ? 'bg-green-500' : ''}`} data-id="element-427" />
              <div className={`bg-amber-500/20 ${severity === 'Minor injury' && likelihood === 'Likely' ? 'bg-amber-500' : ''}`} data-id="element-428" />
            </div>
          </div>
        </div>
      </div>

      <div className="pt-4" data-id="element-429">
        <PrimaryButton onClick={handleContinue} data-id="element-430">Continue</PrimaryButton>
      </div>
    </div>;
}